import axios from 'axios';

// Helper to safely encode paths for URLs
const encodePath = (path) => path.split('/').map(encodeURIComponent).join('/');

// Site Management
export const getSites = async () => {
  try {
    const response = await axios.get('/api/sites/');
    return response.data;
  } catch (error) {
    console.error('Error fetching sites:', error);
    throw error;
  }
};

export const getSiteDetails = async (siteId) => {
  try {
    const response = await axios.get(`/api/sites/${siteId}/details/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching site details:', error);
    throw error;
  }
};

export const createSite = async (siteData) => {
  try {
    const response = await axios.post('/api/sites/', siteData);
    return response.data;
  } catch (error) {
    console.error('Error creating site:', error);
    throw error;
  }
};

export const deleteSite = async (siteId) => {
  try {
    const response = await axios.delete(`/api/sites/${siteId}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting site:', error);
    throw error;
  }
};

export const generateCertificate = async (siteId) => {
  try {
    const response = await axios.post(`/api/sites/${siteId}/generate-cert/`);
    return response.data;
  } catch (error) {
    console.error('Error generating certificate:', error);
    throw error;
  }
};

// File Management
export const listFiles = async (siteId, path = '', recursive = false, target = 'live') => {
  try {
    const response = await axios.get(`/api/sites/${siteId}/files/?path=${encodeURIComponent(path)}&recursive=${recursive}`, { params: { target } });
    return response.data;
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

export const getFileContent = async (siteId, path, target = 'live') => {
  try {
    const response = await axios.get(`/api/sites/${siteId}/files/${encodePath(path)}/`, { params: { target } });
    return response.data;
  } catch (error) {
    console.error('Error getting file content:', error);
    throw error;
  }
};

export const saveFile = async (siteId, path, content, target = 'live') => {
  try {
    const response = await axios.post(`/api/sites/${siteId}/save/${encodePath(path)}/`, { content }, { params: { target } });
    return response.data;
  } catch (error) {
    console.error('Error saving file:', error);
    throw error;
  }
};

export const uploadFile = async (siteId, path, formData) => {
  try {
    const response = await axios.post(`/api/sites/${siteId}/upload/${encodePath(path)}/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

export const createDirectory = async (siteId, directoryName) => {
  try {
    const response = await axios.post(`/api/sites/${siteId}/directories/`, { directory_name: directoryName });
    return response.data;
  } catch (error) {
    console.error('Error creating directory:', error);
    throw error;
  }
};

export const deleteFile = async (siteId, path, recursive = false, target = 'live') => {
  try {
    const response = await axios.delete(`/api/sites/${siteId}/files/${encodePath(path)}/?recursive=${recursive}`, { params: { target } });
    return response.data;
  }  catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};


export const addDomainToSite = async (siteId, domainData) => {
  const response = await fetch(`/api/sites/${siteId}/add-domain/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(domainData),
  });
  
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Failed to add domain');
  }
  
  return response.json();
};

// Email Mappings
export const getEmailMapping = async (siteId, domainName) => {
  try {
    const response = await axios.get(`/api/sites/${siteId}/domains/${domainName}/email_mapping/`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 404) {
      return { email: null }; // Consistent return shape
    }
    if (error.response && error.response.status === 503) {
      // Re-throw with 503 status to indicate service unavailable
      throw error;
    }
    // For connection errors (no response), treat as service unavailable
    if (!error.response) {
      const serviceError = new Error('Email service is currently unavailable');
      serviceError.response = { status: 503 };
      throw serviceError;
    }
    console.error('Error fetching email mapping:', error);
    throw error;
  }
};

// routes is optional: leaving it out keeps the domain's routing rules, [] removes them
export const setEmailMapping = async (siteId, domainName, email, routes) => {
  try {
    const body = routes === undefined ? { email } : { email, routes };
    const response = await axios.post(`/api/sites/${siteId}/domains/${domainName}/email_mapping/`, body);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 503) {
      throw new Error('Email service is currently unavailable. Please try again later.');
    }
    // For connection errors (no response), treat as service unavailable
    if (!error.response) {
      throw new Error('Email service is currently unavailable. Please try again later.');
    }
    console.error('Error setting email mapping:', error);
    const errorMessage = error.response?.data?.text || error.response?.data?.error || 'Failed to set email mapping.';
    throw new Error(errorMessage);
  }
};

export const deleteEmailMapping = async (siteId, domainName) => {
  try {
    const response = await axios.delete(`/api/sites/${siteId}/domains/${domainName}/email_mapping/`);
    return response.data;
  } catch (error) {
    if (error.response && error.response.status === 503) {
      throw new Error('Email service is currently unavailable. Please try again later.');
    }
    // For connection errors (no response), treat as service unavailable  
    if (!error.response) {
      throw new Error('Email service is currently unavailable. Please try again later.');
    }
    console.error('Error deleting email mapping:', error);
    const errorMessage = error.response?.data?.text || error.response?.data?.error || 'Failed to delete email mapping.';
    throw new Error(errorMessage);
  }
};

export const login = async (username, password) => {
  try {
    const response = await axios.post('/api/auth/login', { username, password });
    return response.data;
  } catch (error) {
    console.error('Login failed:', error);
    const errorMessage = error.response?.data?.text || 'Invalid username or password.';
    throw new Error(errorMessage);
  }
};
